import type { ParsedUrlQuery } from 'querystring';

import { parseBooleanQueryParam } from '@/utils/urlUtils';

export type ThirdPartyFlags = {
  disableAlgoliaInsights: boolean;
  disableCampaignTracker: boolean;
  disableGtm: boolean;
  disableOneTrust: boolean;
  disableSentry: boolean;
  disableThirdParties: boolean;
};

export const defaultFlags: ThirdPartyFlags = {
  disableAlgoliaInsights: false,
  disableCampaignTracker: false,
  disableGtm: false,
  disableOneTrust: false,
  disableSentry: false,
  disableThirdParties: false,
};

/**
 * Maps URL query param names to their corresponding flag keys.
 * - disable_third_parties=1    : master kill switch (disables everything below)
 * - disable_algolia_insights=1   : disables Algolia search-insights + sendAlgoliaEvent
 * - disable_campaign_tracker=1  : disables CampaignTracker init
 * - disable_gtm=1               : disables GTM script + dataLayer calls
 * - disable_onetrust=1          : disables OneTrust cookie consent scripts
 * - disable_sentry=1            : disables Sentry error reporting
 */
const FLAG_PARAM_MAP: Record<string, keyof ThirdPartyFlags> = {
  disable_third_parties: 'disableThirdParties',
  disable_algolia_insights: 'disableAlgoliaInsights',
  disable_campaign_tracker: 'disableCampaignTracker',
  disable_gtm: 'disableGtm',
  disable_onetrust: 'disableOneTrust',
  disable_sentry: 'disableSentry',
};

/**
 * Internal: reduce over FLAG_PARAM_MAP and parse each param from a generic
 * getter. Falls back to `fallback` for any param not present in the query.
 */
function parseFlagsFromParams(
  getParam: (key: string) => string | string[] | null | undefined,
  fallback: ThirdPartyFlags = defaultFlags,
): ThirdPartyFlags {
  return Object.entries(FLAG_PARAM_MAP).reduce<ThirdPartyFlags>(
    (acc, [searchKey, flagKey]) => {
      const rawValue = getParam(searchKey);
      if (rawValue === null || rawValue === undefined) {
        return acc;
      }
      return { ...acc, [flagKey]: parseBooleanQueryParam(rawValue) };
    },
    { ...fallback },
  );
}

/**
 * Parse flags from a URL search string (e.g. router.asPath query part).
 * Pass `defaultFlags` as `fallback` so that navigating to a URL without
 * the params resets flags to "all enabled" — no stale state.
 */
export function parseThirdPartyFlagsFromSearch(
  search?: string | null,
  fallback?: ThirdPartyFlags,
): ThirdPartyFlags {
  if (!search) return fallback ?? defaultFlags;
  const normalized = search.startsWith('?') ? search : `?${search}`;
  const params = new URLSearchParams(normalized);
  return parseFlagsFromParams((key) => params.get(key), fallback);
}

/**
 * Parse flags from Next.js ctx.query (used in getInitialProps, SSR).
 * Never stores result — callers pass it as a prop into the React tree.
 */
export function parseThirdPartyFlagsFromQuery(
  query?: ParsedUrlQuery,
  fallback?: ThirdPartyFlags,
): ThirdPartyFlags {
  if (!query) return fallback ?? defaultFlags;
  return parseFlagsFromParams(
    (key) => query[key] as string | undefined,
    fallback,
  );
}

/** Cookie name used to persist the flags across navigation within a session. */
export const THIRD_PARTY_FLAGS_COOKIE = 'bre_third_party_flags';

/**
 * Serialize the enabled disable-flags into the same param format used in the
 * URL (e.g. "disable_gtm=1&disable_sentry=1"). Only flags that are `true` are
 * emitted; an all-default set serializes to an empty string.
 */
export function serializeThirdPartyFlagsToParamString(
  flags: ThirdPartyFlags,
): string {
  return Object.entries(FLAG_PARAM_MAP)
    .filter(([, flagKey]) => flags[flagKey])
    .map(([searchKey]) => `${searchKey}=1`)
    .join('&');
}

/** Extract a single cookie value from a raw Cookie header string. */
function readCookieValue(
  cookieHeader: string | null | undefined,
  name: string,
): string | undefined {
  if (!cookieHeader) return undefined;
  for (const part of cookieHeader.split(';')) {
    const index = part.indexOf('=');
    if (index < 0) continue;
    if (part.slice(0, index).trim() === name) {
      return part.slice(index + 1).trim();
    }
  }
  return undefined;
}

/**
 * Parse flags from a raw Cookie header (works both server-side, from
 * `req.headers.cookie`, and client-side, from `document.cookie`).
 */
export function parseThirdPartyFlagsFromCookie(
  cookieHeader?: string | null,
  fallback?: ThirdPartyFlags,
): ThirdPartyFlags {
  const raw = readCookieValue(cookieHeader, THIRD_PARTY_FLAGS_COOKIE);
  if (!raw) return fallback ?? defaultFlags;
  try {
    return parseThirdPartyFlagsFromSearch(decodeURIComponent(raw), fallback);
  } catch {
    // Malformed cookie (e.g. bad percent-escape) — ignore it rather than
    // throwing, which would break SSR (_app) and the provider effect.
    return fallback ?? defaultFlags;
  }
}

/**
 * Persist the flags as a **session** cookie (no Max-Age/Expires → cleared on
 * browser close). Client-only; a no-op on the server. Writing an all-default
 * set clears the cookie instead.
 */
export function writeThirdPartyFlagsCookie(flags: ThirdPartyFlags): void {
  if (globalThis.window === undefined || typeof document === 'undefined') {
    return;
  }
  const payload = serializeThirdPartyFlagsToParamString(flags);
  if (!payload) {
    clearThirdPartyFlagsCookie();
    return;
  }
  const secure = globalThis.location?.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${THIRD_PARTY_FLAGS_COOKIE}=${encodeURIComponent(
    payload,
  )}; path=/; SameSite=Lax${secure}`;
}

/** Expire the flags cookie (client-only). */
export function clearThirdPartyFlagsCookie(): void {
  if (globalThis.window === undefined || typeof document === 'undefined') {
    return;
  }
  document.cookie = `${THIRD_PARTY_FLAGS_COOKIE}=; path=/; Max-Age=0; SameSite=Lax`;
}

/** Flags are equal when all fields match. */
export const areFlagsEqual = (
  a: ThirdPartyFlags,
  b: ThirdPartyFlags,
): boolean =>
  a.disableThirdParties === b.disableThirdParties &&
  a.disableAlgoliaInsights === b.disableAlgoliaInsights &&
  a.disableCampaignTracker === b.disableCampaignTracker &&
  a.disableGtm === b.disableGtm &&
  a.disableOneTrust === b.disableOneTrust &&
  a.disableSentry === b.disableSentry;

/**
 * Writes Sentry's disable flag into window — browser-only side effect.
 * Safe to call on every flag change; globalThis.window is undefined in Node.
 */
export function seedThirdPartyFlags(flags: ThirdPartyFlags): void {
  if (globalThis.window !== undefined) {
    globalThis.window.__DISABLE_SENTRY__ =
      flags.disableThirdParties || flags.disableSentry;
  }
}

/** True when Algolia analytics should be suppressed. */
export const shouldDisableAlgoliaInsights = (flags: ThirdPartyFlags): boolean =>
  flags.disableThirdParties || flags.disableAlgoliaInsights;

/** True when GTM / dataLayer should be suppressed. */
export const shouldDisableGtm = (flags: ThirdPartyFlags): boolean =>
  flags.disableThirdParties || flags.disableGtm;

/** True when CampaignTracker init should be suppressed. */
export const shouldDisableCampaignTracker = (flags: ThirdPartyFlags): boolean =>
  flags.disableThirdParties || flags.disableCampaignTracker;

/** True when Sentry error reporting should be suppressed. */
export const shouldDisableSentry = (flags: ThirdPartyFlags): boolean =>
  flags.disableThirdParties || flags.disableSentry;

/** True when OneTrust cookie consent scripts should be suppressed. */
export const shouldDisableOneTrust = (flags: ThirdPartyFlags): boolean =>
  flags.disableThirdParties || flags.disableOneTrust;
